import React, { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { Container, UncontrolledTooltip, FormGroup, Label, Input, Button, Row, Col, Card, CardBody } from "reactstrap";
import TableContainer from "components/Common/TableContainer";
import Breadcrumbs from "components/Common/Breadcrumb";
import CustomDatePicker from "common/CustomDatePicker";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { fetchMarkerCancelList, fetchMarkerPendingList, markerApprove, markerCancel, resetAll } from "store/actions";
import { FaCheck } from "react-icons/fa";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import Loader from "common/pageLoader";
import { useLocation,useHistory } from "react-router-dom";
import getAccessRights from "components/Common/getAccessRights";
import useButtonAccess from "components/Common/useButtonAccess";
import { getAttendanceSalaryDate } from "common/attendanceSalaryDate";

const Cancel = () => {
    const [accessRight, setAccessRight] = useState(null);
    const location = useLocation();
    const history = useHistory();
    useEffect(() => {
        const rights = getAccessRights(location.pathname, history);
        setAccessRight(rights?.data);
    }, [location]);
    const { canRead, canCreate, canEdit, canDelete, canApprove } = useButtonAccess(accessRight);
    const {
        markerCancelList,
        loading,
        success,
        error,
        } = useSelector((state) => ({
        markerCancelList: state.AttendanceLeaveManagReducer?.markerCancelList ?? [],

        loading: state.AttendanceLeaveManagReducer?.loading,
        error: state.AttendanceLeaveManagReducer?.error,
        success: state.AttendanceLeaveManagReducer?.success,
    }));

    const dispatch = useDispatch()
    const [timesheetApprovalData, setTimesheetApprovalData] = useState();
    const [authDetails, setAuthDetails] = useState(null);
    const [selectedType, setSelectedType] = useState("1");

    const [initialValues, setInitialValues] = useState({
        type: "1",
        appliedFrom: "",
        appliedTo: "",
    })

    useEffect(() => {
      if (authDetails) {
        getAttendanceSalaryDate(authDetails.unit_id).then(response => {
          if (response.status === "success" && response.data.length > 0) {
            const data = response.data[0];
            setInitialValues(prev => ({
              ...prev,
              appliedFrom: data.from_date.split('/').reverse().join('-'),
              appliedTo: data.to_date.split('/').reverse().join('-')
            }));
          }
        });
      }
    }, [authDetails]);

    useEffect(() => {
          const authUserDetails = useAuthUserDetails();
          setAuthDetails(authUserDetails[0])
          dispatch(resetAll());
    }, [dispatch]);

    useEffect(() => {
        if (markerCancelList && markerCancelList.length > 0) {          
            const initializedData = (markerCancelList || []).map(row => ({
            ...row,
            selectedOption: "Discussion",
            // remarks: "",
            // approved_hours:row.worked_hours,
            }));
            setTimesheetApprovalData(initializedData)
        }else{
            setTimesheetApprovalData([])
        }
    }, [markerCancelList])

    useEffect(() => {
        if (success) {
            handleCancel();
        }
    }, [authDetails,success,dispatch]);

    const validationSchema = Yup.object({
        type: Yup.string().required("Please select one option"),
        appliedFrom: Yup.date().nullable().required("Applied From is required"),
        appliedTo: Yup.date().nullable().required("Applied To is required"),
    });

    const handleBulkActionChange = (action) => {
         const allSelected = timesheetApprovalData.every(
            row => row.selectedOption === action
        );

        const updatedData = (timesheetApprovalData || []).map((row) => ({
        ...row,
        //selectedOption: action,
        selectedOption: allSelected ? null : action,
        }));
        setTimesheetApprovalData(updatedData);
    };
    
    const handleRemarksChange = (sno, value) => {
        const updatedData = (timesheetApprovalData || []).map((item) =>
        item.slip_id === sno ? { ...item, remarks: value } : item
        );
        setTimesheetApprovalData(updatedData);
    };

    const handleOptionChange = (rowId, action) => {
        // // Update the selected option for the specific row
        const updatedData = timesheetApprovalData.map((row) => {
          if (row.slip_id === rowId) {
            return { ...row, 
                selectedOption: row.selectedOption === action ? null : action
            };
          }
          return row;
        });

        setTimesheetApprovalData(updatedData);
    };

    const trailingColumns = [
        { Header: "Applied By", accessor: "applied_by" },
        { Header: "Applied On", accessor: "applied_date" },
        { Header: "Reason", accessor: "reason" }        
    ];

    const baseColumns = [
        
        {
            id: "reject",
            Header: () => (
            <div className="text-center">
                Cancel <br />
                <Input
                type="radio"
                name="selectAll"
                onChange={() => handleBulkActionChange("Reject")}
                />
            </div>
            ),
            Cell: ({ row }) => (
            <div
                className="d-flex justify-content-center align-items-center"
                onClick={() => handleOptionChange(row.original.slip_id, "Reject")}
                style={{ cursor: "pointer" }}
            >
                {row.original.selectedOption === "Reject" ? (
                <FaCheck color="red" size={20} />
                ) : (
                <Input
                    type="radio"
                    name={`action-${row.original.slip_id}`}
                    onChange={() => handleOptionChange(row.original.slip_id, "Reject")}
                    checked={row.original.selectedOption === "Reject"}
                />
                )}
            </div>
            ),
        },
        {
            Header: "Cancel Reason",
            Cell: ({ row }) => {
                const rowIndex = row.index;
                const [value, setValue] = useState(timesheetApprovalData[rowIndex]?.remarks || "");
        
                const handleBlur = () => {
                if (value) {
                    handleRemarksChange(row.original.slip_id, value);
                }
                };
        
                return (
                <Input
                    type="text"
                    bsSize="sm"
                    placeholder="Enter reason"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onBlur={handleBlur} // only update global state on focus out
                />
                );
            }
        },
        { Header: "Slip No.", accessor: "slip_id" } ,
        { Header: "Code", accessor: "emp_no" },
        { Header: "Name", accessor: "emp_name" },
    ];

    const leaveColumns = [
        { Header: "From", accessor: "from_date" },
        { Header: "To", accessor: "to_date" },
        { Header: "Days", accessor: "no_of_days" },
        // { Header: "Type", accessor: "leave_type" },
    ];

    const permissionColumns = [
        { Header: "From Time", accessor: "from_time" },
        { Header: "To Time", accessor: "to_time" },
    ];

    const ondutyColumns = [
        { Header: "From Date", accessor: "from_date" },
        { Header: "From Time", accessor: "from_time" },
        { Header: "To Date", accessor: "to_date" },
        { Header: "To Time", accessor: "to_time" },
    ];

    const getColumnsByType = (selectedType) => {
        let typeSpecific = [];
        switch (selectedType) {
            case "1": // Leave
            typeSpecific = leaveColumns;
            break;
            case "2": // Permission
            typeSpecific = permissionColumns;
            break;
            case "3": // Onduty
            typeSpecific = ondutyColumns;
            break;
            default:
            typeSpecific = [];
        }

        // Insert type-specific fields after the 4th column
        return [...baseColumns, ...typeSpecific, ...trailingColumns];
    };

    const columns = getColumnsByType(selectedType);


    const handleCancel = () => {
        setTimesheetApprovalData([])
        dispatch(resetAll());
        setInitialValues({
            type: "1",
            appliedFrom: "",
            appliedTo: "",
        })
    }

    const handleSubmit = (values, { setSubmitting, resetForm }) => {
        try {
            const formData = new FormData()
            formData.append('type',values?.type)
            formData.append('from_date',values?.appliedFrom)
            formData.append('to_date',values?.appliedTo)
            formData.append('reporting_id',0)
            formData.append('unit_id',authDetails?.unit_id)
            formData.append('create_by',authDetails?.user_id)
            dispatch(fetchMarkerCancelList(formData))
        } catch (error) {
            console.error("Submit error:", error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleLateApprove = () => {
        const updatedCancels = (timesheetApprovalData || [])
            .filter(item => item.selectedOption === "Reject")
            .map(item => ({
                slip_id: item.slip_id?.toString() || "",
                remarks: item.remarks || "",
        }));

        if (updatedCancels.length === 0) {
            Swal.fire({
                icon: "warning",
                title: "No Selection",
                text: "Please select at least one before proceeding.",
            });
            return;
        }

       const missingRemarks = (timesheetApprovalData || [])
            .filter(item => item.selectedOption === "Reject" && (!item.remarks || item.remarks.trim() === ""));

        if (missingRemarks.length > 0) {
            const missingList = missingRemarks
                .map((item) => `Slip ID: ${item.slip_id}`)
                .join(", ");

            Swal.fire({
                icon: "warning",
                title: "Remarks Required",
                text: `Please enter remarks for: ${missingList}`,
            });
            return;
        }

        // 3. Build type-based payload
        let payload = {};
        if (selectedType === "1") {
            payload = { leavecancel: updatedCancels };
        } else if (selectedType === "2") {
            payload = { permissioncancel: updatedCancels };
        } else if (selectedType === "3") {
            payload = { ondutycancel: updatedCancels };
        }

        const convertObject = JSON.stringify(payload);

        // 4. Create FormData if API expects form-data
        const formData = new FormData();
        formData.append("type", selectedType);
        formData.append("items", convertObject);
        formData.append("unit_id", authDetails?.unit_id);
        formData.append("create_by", authDetails?.user_id);

        // 5. Dispatch saga action
        dispatch(markerCancel(formData));
    };


    return (
        <React.Fragment>
            <div className="page-content">
                <Loader loading={loading}/>
                <Container fluid>
                    <Breadcrumbs title="Leave Management" breadcrumbItem="Cancel" />

                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        >
                        {(formik) => {
                            // Update formik values when initialValues change
                            useEffect(() => {
                                if (initialValues.appliedFrom && initialValues.appliedTo) {
                                    formik.setFieldValue("appliedFrom", initialValues.appliedFrom);
                                    formik.setFieldValue("appliedTo", initialValues.appliedTo);
                                }
                            }, [initialValues.appliedFrom, initialValues.appliedTo]);

                            return (
                            <Form>
                            <Row>
                                <Col lg="12">
                                <Card>
                                    <CardBody>
                                    <Row className="align-items-center">
                                        {/* Radio Buttons Section */}
                                        <Col md={3} className="d-flex align-items-center">
                                            <div>
                                            <Field
                                                type="radio"
                                                name="type"
                                                value="1"
                                                className="form-check-input"
                                                checked={formik.values.type === "1"}
                                                onChange={(e) => {
                                                formik.setFieldValue("type", e.target.value);
                                                setSelectedType(e.target.value); // ✅ keep local state in sync
                                                handleCancel()
                                                }}
                                            />{" "}
                                            Leave
                                            </div>

                                            <div>
                                            <Field
                                                type="radio"
                                                name="type"
                                                value="2"
                                                className="form-check-input ms-3"
                                                checked={formik.values.type === "2"}
                                                onChange={(e) => {
                                                formik.setFieldValue("type", e.target.value);
                                                setSelectedType(e.target.value);
                                                handleCancel()
                                                }}
                                            />{" "}
                                            Permission
                                            </div>

                                            <div>
                                            <Field
                                                type="radio"
                                                name="type"
                                                value="3"
                                                className="form-check-input ms-3"
                                                checked={formik.values.type === "3"}
                                                onChange={(e) => {
                                                formik.setFieldValue("type", e.target.value);
                                                setSelectedType(e.target.value);
                                                handleCancel()
                                                }}
                                            />{" "}
                                            Onduty
                                            </div>

                                            {formik.touched.type && formik.errors.type && (
                                            <div className="text-danger ms-2">{formik.errors.type}</div>
                                            )}
                                        </Col>

                                        {/* Applied From */}
                                        <Col md={2}>
                                        <FormGroup>
                                            <Label for="appliedFrom">
                                            Applied From <span className="text-danger">*</span>
                                            </Label>
                                            <CustomDatePicker
                                            name="appliedFrom"
                                            value={formik.values.appliedFrom}
                                            onChange={(date) =>
                                                formik.setFieldValue("appliedFrom", date)
                                            }
                                            onBlur={formik.handleBlur}
                                            />
                                            {formik.touched.appliedFrom &&
                                            formik.errors.appliedFrom && (
                                                <div className="text-danger">
                                                {formik.errors.appliedFrom}
                                                </div>
                                            )}
                                        </FormGroup>
                                        </Col>

                                        {/* Applied To */}
                                        <Col md={2}>
                                        <FormGroup>
                                            <Label for="appliedTo">
                                            Applied To <span className="text-danger">*</span>
                                            </Label>
                                            <CustomDatePicker
                                            name="appliedTo"
                                            value={formik.values.appliedTo}
                                            onChange={(date) =>
                                                formik.setFieldValue("appliedTo", date)
                                            }
                                            onBlur={formik.handleBlur}
                                            />
                                            {formik.touched.appliedTo &&
                                            formik.errors.appliedTo && (
                                                <div className="text-danger">
                                                {formik.errors.appliedTo}
                                                </div>
                                            )}
                                        </FormGroup>
                                        </Col>

                                        {/* Search Button */}
                                        <Col className="text-end mob-center">
                                            {/* Search Button */}
                                            <Button type="submit" className="btn button-search me-2">
                                                <i className="fas fa-search me-1" /> Search
                                            </Button>
                                            <Button type="reset" color="secondary" onClick={handleCancel} className="btn button-cancel me-2"> <i className="fas fa-sync me-1" /> Cancel
                                            </Button>
                                            {canApprove && timesheetApprovalData && timesheetApprovalData.length > 0 ? (
                                                <Button type="button" className="btn button-save" onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleLateApprove();
                                                            }}>
                                                    <i className="fas fa-paper-plane me-1" /> Submit
                                                </Button>
                                            ):''}
                                           
                                        </Col>
                                    </Row>
                                    </CardBody>
                                </Card>

                                <Card>
                                    <CardBody>
                                    {/* <div>
                                        <h5 className="fw-bold text-primary mb-3">
                                        Scheduling Process
                                        </h5>
                                    </div> */}
                                    <TableContainer
                                        columns={columns}
                                        data={
                                            timesheetApprovalData && timesheetApprovalData.length > 0
                                            ? timesheetApprovalData
                                            : []
                                        }
                                        isGlobalFilter={true}
                                        customPageSize={10}
                                        className="custom-header-css"
                                    />
                                    </CardBody>
                                </Card>
                                </Col>
                            </Row>
                            </Form>
                        )}
                        }
                        </Formik>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default Cancel;
